import * as LabelPrimitive from '@radix-ui/react-label';
import { AlertCircle } from 'lucide-react';
import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';

import { cn } from '../../lib/cn';

const CONTROL_BASE =
  'w-full rounded-md border bg-surface-sunken px-3 text-text transition-[border-color,box-shadow,background-color] duration-[160ms] ease-soft placeholder:text-text-faint disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-ring/40';

export interface FieldProps {
  label: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** Right-aligned content in the label row, e.g. a character counter. */
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function Field({ label, htmlFor, hint, error, required, aside, children, className }: FieldProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-baseline justify-between gap-3">
        <LabelPrimitive.Root htmlFor={htmlFor} className="text-text-muted text-sm font-medium">
          {label}
          {required ? <span className="text-danger ml-1">*</span> : null}
        </LabelPrimitive.Root>
        {aside ? <span className="text-2xs text-text-faint">{aside}</span> : null}
      </div>
      {children}
      {error ? (
        <p className="text-danger flex items-start gap-1.5 text-xs">
          <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </p>
      ) : hint ? (
        <p className="text-text-faint text-xs">{hint}</p>
      ) : null}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  leadingIcon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, leadingIcon, ...props },
  ref,
) {
  const control = (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        CONTROL_BASE,
        'h-10 text-sm',
        invalid ? 'border-danger/70 focus:border-danger focus:ring-danger/25' : 'border-line-strong',
        leadingIcon && 'pl-9',
        className,
      )}
      {...props}
    />
  );
  if (!leadingIcon) return control;
  return (
    <div className="relative">
      <span className="text-text-faint pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
        {leadingIcon}
      </span>
      {control}
    </div>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid, rows = 4, ...props },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      aria-invalid={invalid || undefined}
      className={cn(
        CONTROL_BASE,
        'resize-y py-2.5 text-sm leading-relaxed',
        invalid ? 'border-danger/70 focus:border-danger focus:ring-danger/25' : 'border-line-strong',
        className,
      )}
      {...props}
    />
  );
});

/** Convenience wrapper that wires the label, hint and error ids for one input. */
export function TextField({
  label,
  hint,
  error,
  required,
  aside,
  className,
  ...props
}: InputProps & Omit<FieldProps, 'children' | 'htmlFor'>) {
  const id = useId();
  return (
    <Field
      label={label}
      htmlFor={id}
      hint={hint}
      error={error}
      required={required}
      aside={aside}
      className={className}
    >
      <Input id={id} invalid={Boolean(error)} {...props} />
    </Field>
  );
}

export function TextareaField({
  label,
  hint,
  error,
  required,
  aside,
  className,
  ...props
}: TextareaProps & Omit<FieldProps, 'children' | 'htmlFor'>) {
  const id = useId();
  return (
    <Field
      label={label}
      htmlFor={id}
      hint={hint}
      error={error}
      required={required}
      aside={aside}
      className={className}
    >
      <Textarea id={id} invalid={Boolean(error)} {...props} />
    </Field>
  );
}
